/**
 * Terms of Service Page Component
 * 
 * Comprehensive terms of service page for Money Mojo, outlining the terms and
 * conditions for using our financial management platform in accordance with
 * Australian consumer law and applicable regulations.
 * 
 * Features:
 * - Complete terms of service content tailored for financial management app
 * - Dark mode support
 * - Responsive design
 * - Accessibility compliance
 * - Breadcrumb navigation for authenticated users
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function TermsOfServicePage(): React.ReactElement {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleBack = () => {
    // Navigate back to previous page or dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb for authenticated users */}
      {!isLoading && isAuthenticated && (
        <nav aria-label="Breadcrumb" className="flex items-center space-x-2 text-sm">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors focus-ring rounded"
            aria-label="Go back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span>Back</span>
          </button>
          <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">&lt;</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium">Terms of Service</span>
        </nav>
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 scroll-mt-20">
          Terms of Service
        </h1>
        <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
          Our terms of service and usage guidelines
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
          Please read these Terms carefully before using our services. By using Money Mojo, you agree to these conditions.
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8 lg:p-10">
        <article className="prose prose-gray dark:prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              1. Who We Are
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Money Mojo is a digital financial management service offered by Zolandor (ABN 64 159 568 325), a sole trader business registered in Australia. It provides a secure, privacy-focused personal finance management platform with transaction tracking, budgeting tools, AI-powered categorisation, and comprehensive financial insights.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Please read these Terms and Conditions ("Terms") carefully before using the Money Mojo website and platform. By accessing or using this service, you agree to be bound by these Terms. If you do not agree to these Terms, you should not use our platform.
            </p>
          </section>

          {/* Use of the Platform */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              2. Use of the Platform
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              You agree to use Money Mojo only for lawful purposes and in a way that does not infringe the rights of others or interfere with the operation of the platform.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              You must not:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Use the platform in a way that disrupts or damages our services</li>
              <li>Attempt to gain unauthorised access to any systems, accounts, or data</li>
              <li>Copy, scrape, reverse-engineer, or extract any part of the platform, data, or content</li>
              <li>Submit false, misleading, or harmful information or financial data</li>
              <li>Use automated systems or bots to access or interact with the platform without permission</li>
              <li>Share your account credentials with others or create multiple accounts to circumvent platform limits</li>
              <li>Use the platform for any illegal or fraudulent activities</li>
              <li>Interfere with or disrupt the security or functionality of the platform</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
          </section>

          {/* Account Registration and Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              3. Account Registration and Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              To use Money Mojo, you must create an account and provide accurate, current, and complete information. You must be at least 18 years old to create an account, or have parental consent if you are between 16 and 18 years old.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Maintaining the security of your account password and authentication credentials</li>
              <li>All activities that occur under your account</li>
              <li>Ensuring the accuracy of financial data you enter into the platform</li>
              <li>Notifying us immediately of any unauthorised access or security breach</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent or harmful activities.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              4. Intellectual Property
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              All original content on Money Mojo — including the platform design, branding, text, images, layout, software code, AI models, and generated materials — is the intellectual property of Zolandor unless otherwise stated.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Your financial data and content that you upload or enter into the platform remain your property. However, by using the platform, you grant us a limited license to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Store, process, and display your data to provide the financial management services</li>
              <li>Use anonymised, aggregated data to improve our services and generate insights</li>
              <li>Back up and secure your data</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You may not copy, republish, distribute, or create derivative works from any part of the Money Mojo platform, design, or content without written permission from Zolandor.
            </p>
          </section>

          {/* Financial Information Disclaimer */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              5. Financial Information Disclaimer
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Money Mojo provides tools, recommendations, and information for general guidance and educational purposes only. The platform is designed to help you track and manage your personal finances, but it does not constitute professional financial, investment, tax, or legal advice.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Important disclaimers:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>We are not a financial institution, investment advisor, or tax professional</li>
              <li>Budget recommendations, spending insights, and financial projections are estimates based on your input data</li>
              <li>AI-powered categorisation and suggestions are automated tools and may require manual review</li>
              <li>You should consult qualified professionals for personalised financial, investment, tax, or legal advice</li>
              <li>We do not guarantee the accuracy, completeness, or timeliness of financial calculations or insights</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              No guarantees are made regarding results or outcomes based on your use of Money Mojo. You are solely responsible for your financial decisions and for verifying the accuracy of your financial data.
            </p>
          </section>

          {/* Service Availability and Changes */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              6. Availability and Changes
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We aim to keep Money Mojo accessible and operational, but availability is not guaranteed. The platform may be temporarily unavailable due to maintenance, updates, technical issues, or circumstances beyond our control.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Update, modify, or remove features, content, or services at any time without notice</li>
              <li>Suspend or discontinue the platform or any part of it</li>
              <li>Change pricing, subscription terms, or service offerings</li>
              <li>Implement new security measures or access restrictions</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We will make reasonable efforts to notify users of significant changes, but we are not obligated to provide advance notice for all modifications.
            </p>
          </section>

          {/* Privacy and Data */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              7. Privacy and Data
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Use of Money Mojo is subject to our Privacy Policy, which explains how we collect, use, store, and protect your information. By using the platform, you agree to that policy.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Key points:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>We collect only the minimum data necessary to provide financial management services</li>
              <li>Your financial data is encrypted and stored securely</li>
              <li>We do not sell or rent your personal or financial data</li>
              <li>You can access, update, or delete your data at any time through your account settings</li>
              <li>We comply with Australian Privacy Act 1988 and applicable data protection laws</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              For detailed information about our data practices, please review our Privacy Policy.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              To the extent permitted by law, Zolandor (trading as Money Mojo) is not liable for any loss or damage — direct, indirect, incidental, consequential, or special — arising from your use of the platform or its content.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              This includes, but is not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Financial losses resulting from decisions made based on platform insights or recommendations</li>
              <li>Data loss, corruption, or unauthorised access (though we take reasonable security measures)</li>
              <li>Service interruptions, downtime, or technical failures</li>
              <li>Errors or inaccuracies in financial calculations, categorisations, or reports</li>
              <li>Loss of business, revenue, or opportunities</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Our total liability to you for any claims arising from your use of Money Mojo shall not exceed the amount you paid to us in the 12 months preceding the claim, or AUD $100, whichever is greater.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Nothing in these Terms excludes or limits our liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              9. Governing Law
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              These Terms are governed by the laws of New South Wales, Australia. Any disputes arising from or relating to these Terms or your use of Money Mojo will be resolved through the courts of New South Wales, Australia.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If you are a consumer under Australian Consumer Law, you may have certain rights and remedies that cannot be excluded or limited by these Terms. These Terms are subject to the Australian Consumer Law and other applicable consumer protection legislation.
            </p>
          </section>

          {/* Updates to These Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              10. Updates to These Terms
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may revise these Terms from time to time to reflect changes in our services, legal requirements, or business practices. The current version will always be available on this page.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Posting the updated Terms on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification to your registered email address (for significant changes)</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Your continued use of Money Mojo after changes are posted means you accept the updated Terms. If you do not agree to the updated Terms, you should stop using the platform and may close your account.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              11. Contact
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              For questions or concerns about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Email:</strong> admin@moneymojo.finance
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Business:</strong> Zolandor (ABN 64 159 568 325)
              </p>
            </div>
          </section>

          {/* Last Updated */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}

